
import { AdminLayout } from "@/components/admin/AdminLayout";
import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format, subDays, startOfMonth, endOfMonth, subMonths, startOfWeek, endOfWeek, subWeeks, startOfDay, endOfDay } from 'date-fns';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Users, Calendar, Utensils, TrendingUp } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ChefPerformanceReport } from "@/components/admin/reports/ChefPerformanceReport";

const dateRanges = {
  total: { start: new Date(2020, 0, 1), end: new Date() },
  thisMonth: { start: startOfMonth(new Date()), end: new Date() },
  lastMonth: { start: startOfMonth(subMonths(new Date(), 1)), end: endOfMonth(subMonths(new Date(), 1)) },
  last30Days: { start: subDays(new Date(), 30), end: new Date() },
  thisWeek: { start: startOfWeek(new Date()), end: new Date() },
  lastWeek: { start: startOfWeek(subWeeks(new Date(), 1)), end: endOfWeek(subWeeks(new Date(), 1)) },
  yesterday: { start: startOfDay(subDays(new Date(), 1)), end: endOfDay(subDays(new Date(), 1)) },
  today: { start: startOfDay(new Date()), end: new Date() }
};

const AdminReporting = () => {
  const [dateRange, setDateRange] = useState<keyof typeof dateRanges>("total");
  const selectedRange = dateRanges[dateRange];
  
  const { data: transactionData } = useQuery({
    queryKey: ['transaction-stats', dateRange],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('transactions')
        .select('created_at, amount, status')
        .eq('status', 'completed')
        .gte('created_at', selectedRange.start.toISOString())
        .lte('created_at', selectedRange.end.toISOString())
        .order('created_at');
      
      if (error) throw error;
      return data;
    }
  });

  const { data: mealPurchases } = useQuery({
    queryKey: ['meal-purchases', dateRange],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('transactions')
        .select('created_at, meal_id, status')
        .eq('status', 'completed')
        .gte('created_at', selectedRange.start.toISOString())
        .lte('created_at', selectedRange.end.toISOString())
        .order('created_at');
      
      if (error) throw error;
      return data;
    }
  });

  const { data: userStats } = useQuery({
    queryKey: ['user-stats', dateRange],
    queryFn: async () => {
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, created_at')
        .gte('created_at', selectedRange.start.toISOString())
        .lte('created_at', selectedRange.end.toISOString());
      
      if (profilesError) throw profilesError;
      return profiles;
    }
  });

  const { data: activeUsers, isLoading: isLoadingActiveUsers } = useQuery({
    queryKey: ['active-users'],
    queryFn: async () => {
      try {
        const now = new Date();
        const dayAgo = subDays(now, 1);
        const weekAgo = subDays(now, 7);
        const monthAgo = subDays(now, 30);
        
        // Query auth.sessions table to find active user IDs by last activity
        const { data: sessions, error: sessionsError } = await supabase
          .from('profiles')
          .select('id, updated_at');
          
        if (sessionsError) {
          console.error('Error fetching user sessions:', sessionsError);
          throw sessionsError;
        }
        
        if (!sessions || sessions.length === 0) {
          return { dailyActive: 0, weeklyActive: 0, monthlyActive: 0 };
        }
        
        const dailyActive = sessions.filter(session => 
          new Date(session.updated_at) >= dayAgo
        ).length;
        
        const weeklyActive = sessions.filter(session => 
          new Date(session.updated_at) >= weekAgo
        ).length;
        
        const monthlyActive = sessions.filter(session => 
          new Date(session.updated_at) >= monthAgo
        ).length;
        
        return { dailyActive, weeklyActive, monthlyActive };
      } catch (error) {
        console.error('Error computing active users:', error);
        return { dailyActive: 0, weeklyActive: 0, monthlyActive: 0 };
      }
    }
  });

  const handleDateRangeChange = (value: string) => {
    setDateRange(value as keyof typeof dateRanges);
  };

  const ReportCard = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <Card className="p-6">
      <h2 className="text-xl font-semibold text-black mb-4">{title}</h2>
      {children}
    </Card>
  );

  const formatChartData = (data: any[], dateRange: { start: Date; end: Date }) => {
    if (!data) return [];

    const filteredData = data.filter(item => {
      const date = new Date(item.created_at);
      return date >= dateRange.start && date <= dateRange.end;
    });

    return filteredData.map(item => ({
      date: format(new Date(item.created_at), 'MM/dd/yyyy'),
      value: item.amount || 1
    }));
  };

  const ChartSection = ({ data, dateRange }: { data: any[]; dateRange: { start: Date; end: Date } }) => (
    <div className="h-[400px] w-full">
      <ResponsiveContainer>
        <BarChart data={formatChartData(data || [], dateRange)}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="date" />
          <YAxis />
          <Tooltip />
          <Bar dataKey="value" fill="#3b82f6" />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );

  return (
    <AdminLayout title="Reporting">
      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4" /> Overview
          </TabsTrigger>
          <TabsTrigger value="transactions" className="flex items-center gap-2">
            <Calendar className="h-4 w-4" /> Transactions
          </TabsTrigger>
          <TabsTrigger value="users" className="flex items-center gap-2">
            <Users className="h-4 w-4" /> Users
          </TabsTrigger>
          <TabsTrigger value="chefs" className="flex items-center gap-2">
            <Utensils className="h-4 w-4" /> Chef Performance
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="overview" className="space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <Users className="h-5 w-5 text-primary" />
                <div>
                  <p className="text-sm text-gray-600">Daily Active Users</p>
                  <p className="text-2xl font-semibold text-black">{isLoadingActiveUsers ? '...' : activeUsers?.dailyActive || 0}</p>
                </div>
              </div>
            </Card>
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <Users className="h-5 w-5 text-primary" />
                <div>
                  <p className="text-sm text-gray-600">Weekly Active Users</p>
                  <p className="text-2xl font-semibold text-black">{isLoadingActiveUsers ? '...' : activeUsers?.weeklyActive || 0}</p>
                </div>
              </div>
            </Card>
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <Users className="h-5 w-5 text-primary" />
                <div>
                  <p className="text-sm text-gray-600">Monthly Active Users</p>
                  <p className="text-2xl font-semibold text-black">{isLoadingActiveUsers ? '...' : activeUsers?.monthlyActive || 0}</p>
                </div>
              </div>
            </Card>
          </div>
        </TabsContent>
        
        <TabsContent value="transactions" className="space-y-8">
          <ReportCard title="Transaction Volume">
            <Select value={dateRange} onValueChange={handleDateRangeChange}>
              <SelectTrigger className="w-full md:w-[200px] mb-4">
                <SelectValue placeholder="Select date range" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(dateRanges).map(([key, range]) => (
                  <SelectItem key={key} value={key}>
                    {key.replace(/([A-Z])/g, ' $1').toLowerCase()}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="mt-4">
              <ChartSection data={transactionData || []} dateRange={selectedRange} />
            </div>
          </ReportCard>
        </TabsContent>
        
        <TabsContent value="users" className="space-y-8">
          <ReportCard title="User Activity">
            <Select value={dateRange} onValueChange={handleDateRangeChange}>
              <SelectTrigger className="w-full md:w-[200px] mb-4">
                <SelectValue placeholder="Select date range" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(dateRanges).map(([key, range]) => (
                  <SelectItem key={key} value={key}>
                    {key.replace(/([A-Z])/g, ' $1').toLowerCase()}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="mt-4">
              <ChartSection data={userStats || []} dateRange={selectedRange} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
              <Card className="p-4">
                <div className="flex items-center gap-3">
                  <Users className="h-5 w-5 text-primary" />
                  <div>
                    <p className="text-sm text-gray-600">Daily Active Users</p>
                    <p className="text-2xl font-semibold text-black">{isLoadingActiveUsers ? '...' : activeUsers?.dailyActive || 0}</p>
                  </div>
                </div>
              </Card>
              <Card className="p-4">
                <div className="flex items-center gap-3">
                  <Users className="h-5 w-5 text-primary" />
                  <div>
                    <p className="text-sm text-gray-600">Weekly Active Users</p>
                    <p className="text-2xl font-semibold text-black">{isLoadingActiveUsers ? '...' : activeUsers?.weeklyActive || 0}</p>
                  </div>
                </div>
              </Card>
              <Card className="p-4">
                <div className="flex items-center gap-3">
                  <Users className="h-5 w-5 text-primary" />
                  <div>
                    <p className="text-sm text-gray-600">Monthly Active Users</p>
                    <p className="text-2xl font-semibold text-black">{isLoadingActiveUsers ? '...' : activeUsers?.monthlyActive || 0}</p>
                  </div>
                </div>
              </Card>
            </div>
          </ReportCard>
        </TabsContent>
        
        <TabsContent value="chefs" className="space-y-8">
          <ReportCard title="Chef Performance">
            <ChefPerformanceReport />
          </ReportCard>
        </TabsContent>
      </Tabs>
    </AdminLayout>
  );
};

export default AdminReporting;
